import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Label } from '../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import { 
  ArrowLeft, FloppyDisk, EnvelopeSimple, Pencil,
  Calendar, Sparkle, Copy, WhatsappLogo
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Textarea } from '../components/ui/textarea';

import { API } from '../config/api';
import QuoteFollowupsCard from '../components/QuoteFollowupsCard';
import WhatsAppSendDialog from '../components/WhatsAppSendDialog';

export default function QuoteDetailPage() {
  const [waOpen, setWaOpen] = useState(false);
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(true);
  const [generatingEmail, setGeneratingEmail] = useState(false);
  const [generatedEmail, setGeneratedEmail] = useState(null);
  const [emailType, setEmailType] = useState('proposal');

  useEffect(() => {
    const fetchQuote = async () => {
      try {
        const { data } = await axios.get(`${API}/quotes/${id}`);
        setQuote(data || {})
      } catch (err) {
        console.error('Error fetching quote:', err);
        toast.error(t('quoteDetail.notFound'));
        navigate('/quotes');
      } finally {
        setLoading(false);
      }
    };
    fetchQuote();
  }, [id, navigate]);

  // Load PDF for preview
  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    const loadPdf = async () => {
      setPdfLoading(true);
      try {
        const response = await axios.get(`${API}/quotes/${id}/pdf`, {
          responseType: 'blob'
        });
        if (cancelled) return;
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        setPdfUrl(url);
      } catch (err) {
        console.error('Error loading PDF preview:', err);
      } finally {
        if (!cancelled) setPdfLoading(false);
      }
    };
    loadPdf();
    return () => { cancelled = true; };
  }, [id]);

  // Cleanup blob URL on unmount
  useEffect(() => {
    return () => {
      if (pdfUrl) window.URL.revokeObjectURL(pdfUrl);
    };
  }, [pdfUrl]);

  const handleStatusChange = async (newStatus) => {
    try {
      await axios.put(`${API}/quotes/${id}`, { status: newStatus });
      setQuote({ ...quote, status: newStatus });
      toast.success(t('quoteDetail.statusUpdated'));
    } catch (err) {
      console.error('Error updating status:', err);
      toast.error(t('quoteDetail.statusError'));
    }
  };

  const handleSavePdf = () => {
    if (!pdfUrl || !quote) return;
    const link = document.createElement('a');
    link.href = pdfUrl;
    link.download = `preventivo_${quote.quote_number}.pdf`;
    link.click();
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const locale = i18n.language === 'es' ? 'es-ES' : 'it-IT';
    return date.toLocaleDateString(locale, { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const handleGenerateEmail = async (type) => {
    setEmailType(type);
    setGeneratingEmail(true);
    try {
      const itemsSummary = (quote.items || []).map(i => `${i.product_name?.split(' - ')[0]} x${i.quantity}`).join(', ');
      const { data } = await axios.post(`${API}/ai/generate-email`, {
        client_name: quote.client_name,
        quote_number: quote.quote_number,
        total: quote.total,
        items_summary: itemsSummary,
        email_type: type
      });
      setGeneratedEmail(data);
    } catch (err) {
      toast.error(t('quoteDetail.emailGenError'));
    } finally {
      setGeneratingEmail(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success(t('quoteDetail.copied'));
  };

  const handleDuplicate = async () => {
    try {
      const { data } = await axios.post(`${API}/quotes/${id}/duplicate`);
      toast.success(t('quoteDetail.duplicated', { number: data.quote_number }));
      navigate(`/quotes/${data.id}`);
    } catch (err) {
      toast.error(t('quoteDetail.duplicateError'));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0033A0]"></div>
      </div>
    );
  }

  const statusLabels = {};
  const statusColors = {
    draft: 'bg-gray-100 text-gray-800 border-gray-200',
    sent: 'bg-blue-100 text-blue-800 border-blue-200',
    accepted: 'bg-green-100 text-green-800 border-green-200',
    rejected: 'bg-red-100 text-red-800 border-red-200',
    won: 'bg-green-100 text-green-800 border-green-200',
    lost: 'bg-red-100 text-red-800 border-red-200'
  };
  ['draft','sent','accepted','rejected','won','lost'].forEach(s => {
    statusLabels[s] = t(`quoteDetail.statusLabels.${s}`);
  });

  if (!quote) return null;

  return (
    <div className="space-y-6" data-testid="quote-detail-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            onClick={() => navigate('/quotes')}
            className="text-[#4A5568]"
            data-testid="back-button"
          >
            <ArrowLeft size={20} />
          </Button>
          <div>
            <h1 className="font-outfit text-2xl sm:text-3xl font-bold text-[#0A0F1A]">
              {quote.quote_number}
            </h1>
            <p className="text-[#4A5568] mt-1">
              {t('quoteDetail.createdOn')} {formatDate(quote.created_at)}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={quote.status} onValueChange={handleStatusChange}>
            <SelectTrigger className={`w-32 sm:w-40 border ${statusColors[quote.status]}`} data-testid="status-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(statusLabels).map(([value, label]) => (
                <SelectItem key={value} value={value}>{label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            onClick={handleSavePdf}
            disabled={!pdfUrl}
            className="border-[#E2E8F0] text-xs sm:text-sm"
            data-testid="download-pdf-button"
          >
            <FloppyDisk size={16} className="mr-1 sm:mr-2" />
            <span className="hidden sm:inline">Salva </span>PDF
          </Button>
          <Button
            variant="outline"
            onClick={() => setWaOpen(true)}
            className="border-[#25D366] text-[#128C4B] text-xs sm:text-sm"
            data-testid="whatsapp-quote-button"
          >
            <WhatsappLogo size={16} weight="fill" className="mr-1 sm:mr-2 text-[#25D366]" />
            WhatsApp
          </Button>
          <Button
            variant="outline"
            onClick={handleDuplicate}
            className="border-[#E2E8F0] text-xs sm:text-sm"
            data-testid="duplicate-quote-button"
          >
            <Copy size={16} className="mr-1 sm:mr-2" />
            <span className="hidden sm:inline">{t('quoteDetail.duplicate')}</span>
            <span className="sm:hidden">Copia</span>
          </Button>
          <Button
            onClick={() => navigate(`/quotes/${id}/edit`)}
            className="bg-[#0033A0] hover:bg-[#0047AB] text-white text-xs sm:text-sm"
            data-testid="edit-quote-button"
          >
            <Pencil size={16} className="mr-1 sm:mr-2" />
            {t('quoteDetail.edit')}
          </Button>
        </div>
      </div>

      <QuoteFollowupsCard quoteId={id} status={quote.status} />
      <WhatsAppSendDialog open={waOpen} onOpenChange={setWaOpen} quote={quote} />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Validity */}
          <Card className="border border-[#E2E8F0] rounded-sm">
            <CardContent className="p-4 flex items-center gap-4">
              <Calendar size={20} className="text-[#4A5568] shrink-0" />
              <div className="flex items-center gap-6 flex-wrap">
                <div>
                  <p className="text-xs text-[#4A5568]">{t('quoteDetail.validity')}</p>
                  <p className="font-medium text-[#0A0F1A]">{quote.validity_days} {t('quoteDetail.days')}</p>
                </div>
                <div>
                  <p className="text-xs text-[#4A5568]">{t('quoteDetail.expiresOn')}</p>
                  <p className="font-medium text-[#0A0F1A]">{formatDate(new Date(new Date(quote.created_at).getTime() + quote.validity_days * 24 * 60 * 60 * 1000).toISOString())}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* AI Email Generator - compact */}
          <Card className="border border-[#E2E8F0] rounded-sm">
            <CardContent className="p-4">
              <div className="flex items-center gap-3 mb-3">
                <Sparkle size={18} className="text-[#0033A0] shrink-0" weight="fill" />
                <span className="font-outfit text-sm font-semibold text-[#0A0F1A]">{t('quoteDetail.generateEmailAi')}</span>
              </div>
              <div className="flex gap-2 flex-wrap">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleGenerateEmail('proposal')}
                  disabled={generatingEmail}
                  className="border-[#E2E8F0] text-xs h-8"
                  data-testid="generate-email-proposal"
                >
                  <EnvelopeSimple size={14} className="mr-1.5 text-[#0033A0]" />
                  {t('quoteDetail.proposal')}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleGenerateEmail('followup')}
                  disabled={generatingEmail}
                  className="border-[#E2E8F0] text-xs h-8"
                  data-testid="generate-email-followup"
                >
                  <EnvelopeSimple size={14} className="mr-1.5 text-green-600" />
                  {t('quoteDetail.followup')}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleGenerateEmail('reminder')}
                  disabled={generatingEmail}
                  className="border-[#E2E8F0] text-xs h-8"
                  data-testid="generate-email-reminder"
                >
                  <EnvelopeSimple size={14} className="mr-1.5 text-amber-600" />
                  {t('quoteDetail.reminder')}
                </Button>
              </div>
            </CardContent>
          </Card>
      </div>

      {/* Generated Email - full width below if present */}
      {generatingEmail && (
        <div className="flex items-center justify-center gap-2 py-4">
          <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#0033A0]" />
          <span className="text-sm text-[#4A5568]">{t('quoteDetail.generating')}</span>
        </div>
      )}
      {generatedEmail && !generatingEmail && (
        <Card className="border border-[#E2E8F0] rounded-sm">
          <CardContent className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <div className="flex items-center justify-between mb-1">
                  <Label className="text-xs font-semibold text-[#4A5568]">{t('quoteDetail.subjectLabel')}</Label>
                  <button onClick={() => copyToClipboard(generatedEmail.subject)} className="text-[#0033A0] hover:text-[#0047AB]">
                    <Copy size={14} />
                  </button>
                </div>
                <p className="text-sm font-medium text-[#0A0F1A] bg-[#F4F6F8] p-2 rounded-sm" data-testid="email-subject">
                  {generatedEmail.subject}
                </p>
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <Label className="text-xs font-semibold text-[#4A5568]">{t('quoteDetail.bodyLabel')}</Label>
                  <button onClick={() => copyToClipboard(generatedEmail.body)} className="text-[#0033A0] hover:text-[#0047AB]">
                    <Copy size={14} />
                  </button>
                </div>
                <Textarea
                  value={generatedEmail.body}
                  readOnly
                  rows={5}
                  className="text-sm bg-[#F4F6F8] resize-none"
                  data-testid="email-body"
                />
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="mt-3 border-[#0033A0] text-[#0033A0]"
              onClick={() => copyToClipboard(`${t('quoteDetail.subjectLabel')}: ${generatedEmail.subject}\n\n${generatedEmail.body}`)}
              data-testid="copy-full-email"
            >
              <Copy size={16} className="mr-2" />
              {t('quoteDetail.copyFullEmail')}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* PDF Preview - Full Width */}
      <Card className="border border-[#E2E8F0] rounded-sm overflow-hidden" data-testid="pdf-preview-area">
        <CardContent className="p-0">
          {pdfLoading ? (
            <div className="flex flex-col items-center justify-center h-[50vh] sm:h-[80vh] bg-[#F4F6F8]">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0033A0] mb-3"></div>
              <p className="text-sm text-[#4A5568]">Caricamento anteprima...</p>
            </div>
          ) : pdfUrl ? (
            <iframe
              src={pdfUrl}
              title="Anteprima Preventivo"
              className="w-full border-0 h-[50vh] sm:h-[80vh]"
              data-testid="pdf-iframe"
            />
          ) : (
            <div className="flex flex-col items-center justify-center h-[50vh] sm:h-[80vh] bg-[#F4F6F8]">
              <p className="text-sm text-[#4A5568]">Impossibile caricare l'anteprima PDF</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

